/**
 * Pending Approval Page
 * Full-screen page shown when user's account is not yet approved by admin.
 * Displays different messages based on accountStatus: pending, rejected, suspended.
 */

import { useState } from 'react'
import { Clock, XCircle, ShieldOff, LogOut, Mail } from 'lucide-react'
import { useAuth } from '@contexts/AuthContext'
import { useConfig } from '@contexts/ConfigContext'
import { formatReleaseDate } from '@utils/releaseCampaign'

const STATUS_CONFIG = {
  pending: {
    icon: Clock,
    iconColor: 'text-amber-500',
    bgColor: 'bg-amber-100 dark:bg-amber-900/20',
    title: 'Account Pending Approval',
    message:
      'Your access request is awaiting x3Algo review and account activation.',
    hint: 'We will email you when your account is ready.',
  },
  rejected: {
    icon: XCircle,
    iconColor: 'text-red-500',
    bgColor: 'bg-red-100 dark:bg-red-900/20',
    title: 'Account Not Approved',
    message:
      'Your account application was not approved. If you believe this is an error, please contact support.',
    hint: null,
  },
  suspended: {
    icon: ShieldOff,
    iconColor: 'text-orange-500',
    bgColor: 'bg-orange-100 dark:bg-orange-900/20',
    title: 'Account Suspended',
    message:
      'Your account has been suspended. Please contact support for more information.',
    hint: null,
  },
}

function ReleaseCampaignTimeline({ campaign }) {
  if (campaign.status === 'planned') {
    const formattedDate = formatReleaseDate(campaign.releaseDate)

    if (!campaign.statusCopy?.plannedPrefix || !formattedDate) {
      return null
    }

    return (
      <p className="mt-4 text-xs text-gray-400 [overflow-wrap:anywhere] dark:text-gray-500">
        {campaign.statusCopy.plannedPrefix}{' '}
        <time dateTime={campaign.releaseDate}>{formattedDate}</time>.
      </p>
    )
  }

  const message =
    campaign.status === 'active'
      ? campaign.statusCopy?.active
      : campaign.status === 'delayed'
        ? campaign.statusCopy?.delayed
        : null

  return message ? (
    <p className="mt-4 text-xs text-gray-400 [overflow-wrap:anywhere] dark:text-gray-500">
      {message}
    </p>
  ) : null
}

function PendingApprovalPage() {
  const { user, logout, resendVerification } = useAuth()
  const { releaseCampaign } = useConfig()
  const [resendStatus, setResendStatus] = useState(null) // 'sending' | 'sent' | 'error'
  const status = user?.accountStatus || 'pending'
  const releasePendingApproval =
    releaseCampaign?.enabled &&
    releaseCampaign.pendingApproval?.title &&
    releaseCampaign.pendingApproval.message
      ? releaseCampaign.pendingApproval
      : null
  const baseConfig = STATUS_CONFIG[status] || STATUS_CONFIG.pending
  const config =
    status === 'pending' && releasePendingApproval
      ? {
          ...baseConfig,
          title: releasePendingApproval.title,
          message: releasePendingApproval.message,
          hint: null,
        }
      : baseConfig
  const Icon = config.icon
  const reason = user?.accountStatusReason

  const handleResend = async () => {
    setResendStatus('sending')
    try {
      const result = await resendVerification()
      setResendStatus(result?.success ? 'sent' : 'error')
    } catch {
      setResendStatus('error')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-8 text-center">
        <div
          className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full ${config.bgColor}`}
        >
          <Icon className={`h-8 w-8 ${config.iconColor}`} />
        </div>

        <h2 className="mt-6 text-xl font-semibold text-gray-900 [overflow-wrap:anywhere] dark:text-white">
          {config.title}
        </h2>

        <p className="mt-3 text-sm text-gray-600 [overflow-wrap:anywhere] dark:text-gray-400">
          {config.message}
        </p>

        {reason && status !== 'pending' && (
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Reason: {reason}
            </p>
          </div>
        )}

        {config.hint && (
          <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
            {config.hint}
          </p>
        )}

        {status === 'pending' && releasePendingApproval && (
          <ReleaseCampaignTimeline campaign={releaseCampaign} />
        )}

        {status === 'pending' && !user?.isVerified && (
          <div className="mt-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-md">
            <p className="text-xs text-primary-600 dark:text-primary-400 mb-2">
              Haven&apos;t received your verification email?
            </p>
            <button
              onClick={handleResend}
              disabled={resendStatus === 'sending' || resendStatus === 'sent'}
              className="inline-flex items-center space-x-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50 disabled:no-underline"
            >
              <Mail className="w-3.5 h-3.5" />
              <span>
                {resendStatus === 'sending'
                  ? 'Sending...'
                  : resendStatus === 'sent'
                    ? 'Email queued! Check your inbox.'
                    : resendStatus === 'error'
                      ? 'Could not send. Try again.'
                      : 'Resend verification email'}
              </span>
            </button>
          </div>
        )}

        <div className="mt-8 space-y-3">
          <button
            onClick={logout}
            className="w-full flex items-center justify-center space-x-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-2.5 px-4 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </div>

        <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">
          Need help? Contact us at{' '}
          <a
            href="mailto:contact@x3algo.com?subject=Account%20approval"
            className="text-violet-500 hover:underline"
          >
            contact@x3algo.com
          </a>
        </p>
      </div>
    </div>
  )
}

export default PendingApprovalPage
